import {
  Component,
  Renderer2,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ChangeDetectorRef,
  ViewChild
} from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { QuestionBase } from './question-base';
import { TranslateService } from '@ngx-translate/core';
import { QuestionControlService } from '../../services/forms/question-control.service';

@Component({
  selector: 'lms-question',
  templateUrl: './dynamic-form-question.component.html',
  styleUrls: ['./dynamic-form-question.component.scss']
})
export class DynamicFormQuestionComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input() question: QuestionBase<any>;
  @Input() action: string;
  @Input() form: UntypedFormGroup;
  @ViewChild('translationModal', { static: false }) translationModal: any;
  public questionKey: string;

  public listeners = [];
  public isRequired = true;
  currentClasses: any;

  private control: AbstractControl;

  constructor(
    public elementRef: ElementRef,
    public renderer: Renderer2,
    public translate: TranslateService,
    public qcs: QuestionControlService,
    public cd: ChangeDetectorRef
  ) {}

  ngOnInit() {
    if (this.question) {
      this.question.form = this.form;
      const required = this.question.validators && this.question.validators.required;
      this.currentClasses = {
        required: required,
        validate: true
      };
      this.control = this.form.controls[this.question.key];
    }
  }

  ngOnChanges() {
    if (this.question) {
      this.questionKey = this.question.key; // `${this.action}.${this.question.key || Date.now()}`;
      this.qcs.createControl(this.question, this.form, this.action);
    }
  }

  hasValue() {
    if (this.question && this.question.value) {
      const { value } = this.question;
      if (value instanceof Array) {
        return value.length > 0;
      }
      return !!value;
    }
    return false;
  }

  get isValid() {
    if (!this.question) return true;
    if (this.question.key) {
      return !this.question.control.invalid || (this.question.control.pristine && !this.hasValue());
    }
    return !this.question.errors || this.question.errors.length === 0;
  }

  get errors() {
    const errors = this.question.key ? this.question.control.errors : this.question.errors ? this.question.errors : [];
    const messages = [];
    if (errors) {
      Object.keys(errors).forEach((error) =>
        messages.push({
          message: errors[error] && errors[error].label ? errors[error].label : 'error.' + error,
          params: errors[error]
        })
      );
    }

    return messages;
  }

  get error() {
    const errors = this.question.key ? this.question.control.errors : this.question.errors ? this.question.errors : [];
    const messages = [];
    if (errors) {
      Object.keys(errors).forEach((error) =>
        messages.push({
          message: errors[error] && errors[error].label ? errors[error].label : 'error.' + error,
          params: errors[error]
        })
      );
    }

    return messages && messages.length ? messages[0] : {};
  }

  get errorMessage() {
    if (this.isValid) return '';
    const errors = this.errors;
    const error = errors && errors.length ? errors[0] : null;
    return error ? this.translate.instant(error.message, error.params) : '';
  }

  ngAfterViewInit() {
    this.clearListeners();
    this.cd.detectChanges();
    // DEPRECATED for skeletor components
    if (
      this.question &&
      !this.question.designSystem &&
      this.question.key &&
      this.question.controlType !== 'group' &&
      this.question.eventHandlers
    ) {
      const component: Element = this.elementRef.nativeElement.querySelector('#' + this.question.id);
      this.listeners = Object.keys(this.question.eventHandlers)
        .map((eventHandler) => {
          if (component) {
            return this.renderer.listen(
              component,
              eventHandler,
              ((event) => {
                this.question.eventHandlers[eventHandler](event, this);
              }).bind(this)
            );
          }
          return null;
        })
        .filter((l) => !!l);
      this.cd.detectChanges();
    }
  }

  // deprecated for skeletor components
  fireQuestionEvents(data) {
    this.updateChildQuestions(data);
    if (this.question.eventHandlers && this.question.eventHandlers.onSelected) {
      this.question.eventHandlers.onSelected(data);
    }
  }

  updateChildQuestions(data) {
    if (this.question.childQuestions && this.question.childQuestions.length) {
      this.question.childQuestions.forEach((childQuestion) => {
        if (childQuestion.key) {
          const control = this.form.controls[childQuestion.key];
          if (data && data[childQuestion.source]) {
            control.reset(data[childQuestion.source]);
            control.markAsDirty();
          } else {
            control.reset();
          }
          control.markAsDirty();
        }
      });
    }
  }

  ngOnDestroy() {
    this.clearListeners();
  }

  clearListeners() {
    this.listeners.forEach((listener) => listener());
  }

  confirmTranslation(event) {
    if (this.question.eventHandlers && this.question.eventHandlers.onConfirmTranslation) {
      this.question.eventHandlers.onConfirmTranslation(event);
    }
  }
}
